import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Star, Tent, ChevronDown, ChevronUp, MessageSquare, Plus, Send, Loader2, Edit, Map, AlertTriangle } from 'lucide-react';
import ReviewPhotosDisplay from './ReviewPhotosDisplay';
import ReviewPhotoUpload from './ReviewPhotoUpload';

const CampDetailsView = () => {
    const { camp_location_id } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [campData, setCampData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [amenitiesExpanded, setAmenitiesExpanded] = useState(false);
    const [reviewsExpanded, setReviewsExpanded] = useState(false);
    const [reviews, setReviews] = useState([]);
    const [reviewsLoading, setReviewsLoading] = useState(false);
    const [selectedPhoto, setSelectedPhoto] = useState(null);
    const [checkingIn, setCheckingIn] = useState(false);
    const [userId, setUserId] = useState(null);
    const [ createdBy, setCreatedBy] = useState(null);
    const [checkins, setCheckins] = useState([]);
    const [checkinsLoading, setCheckinsLoading] = useState(false);
    const [showAddReview, setShowAddReview] = useState(false);
    const [newReview, setNewReview] = useState({
        title: '',
        review_text: '',
        overall_rating: 5,
        cleanliness_rating: 5,
        facilities_rating: 5,
        location_rating: 5,
        value_rating: 5,
        would_recommend: true,
        visit_date: '',
        stay_duration_days: 1,
        group_size: 1,
        camping_type: 'tent',
        photos: []
    });
    const [submittingReview, setSubmittingReview] = useState(false);
    const [reviewPhotos, setReviewPhotos] = useState([]);

    // Map state preservation functions
    const getReturnMapState = useCallback(() => {
        const returnLat = searchParams.get('returnLat');
        const returnLng = searchParams.get('returnLng');
        const returnZoom = searchParams.get('returnZoom');
        const returnFrom = searchParams.get('returnFrom');

        if (returnLat && returnLng && returnZoom && (returnFrom === 'map' || returnFrom === 'addLocation')) {
            return {
                lat: parseFloat(returnLat),
                lng: parseFloat(returnLng),
                zoom: parseFloat(returnZoom)
            };
        }
        return null;
    }, [searchParams]);

    // Function to navigate back to map with preserved state
    const goBackToMap = useCallback(() => {
        const mapState = getReturnMapState();

        if (mapState) {
            // Navigate back to map with preserved location and zoom
            const params = new URLSearchParams();
            params.set('returnLat', mapState.lat.toString());
            params.set('returnLng', mapState.lng.toString());
            params.set('returnZoom', mapState.zoom.toString());
            params.set('returnFrom', 'map');

            navigate(`/events/camps?${params.toString()}`);
        } else {
            // Fallback to regular map navigation
            navigate('/events/camps');
        }
    }, [navigate, getReturnMapState]);

    useEffect(() => {
        fetchCampDetails();
        fetchRecentCheckins();
    }, [camp_location_id]);

    const fetchCampDetails = async () => {
        try {
            const response = await fetch(`/otgservices/getCampDetails.php?camp_location_id=${camp_location_id}`);
            const data = await response.json();
            
            if (data.success) {
                setCampData(data.data);
                // Set user_id from the API response
                setUserId(data.user_id);
                setCreatedBy(data.createdBy);
            } else {
                setError('Failed to load camp details');
            }
        } catch (err) {
            setError('Failed to fetch camp details');
        } finally {
            setLoading(false);
        }
    };

    const renderStars = (rating, totalRatings) => {
        const stars = [];
        const fullStars = Math.floor(rating);
        const hasHalfStar = rating % 1 !== 0;
        
        for (let i = 0; i < 5; i++) {
            if (i < fullStars) {
                stars.push(<Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />);
            } else if (i === fullStars && hasHalfStar) {
                stars.push(<Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400 opacity-50" />);
            } else {
                stars.push(<Star key={i} className="w-4 h-4 text-gray-300" />);
            }
        }
        
        return (
            <div className="flex items-center gap-1">
                <div className="flex">{stars}</div>
                <span className="text-sm text-gray-600 ml-2">
                    {rating} ({totalRatings} ratings)
                </span>
            </div>
        );
    };

    const getAmenityIcon = (amenityName, icon) => {
        const iconMap = {
            // Utilities
            'water': '💧',
            'power': '⚡',
            'sewer': '🚰',
            'dump': '🚮',
            'wifi': '📶',
            'cell': '📱',
            'comms': '🛰️', // Starlink
            
            // Facilities
            'restroom': '🚻',
            'shower': '🚿',
            'toilet': '🚽',
            'vault': '🏠',
            'table': '🪑',
            'fire': '🔥',
            'grill': '🍖',
            'bear': '🧳',
            'trash': '🗑️',
            
            // Activities
            'hiking': '🥾',
            'fishing': '🎣',
            'swimming': '🏊',
            'boat': '🛥️',
            'bike': '🚴',
            'atv': '🏍️',
            
            // Services
            'store': '🏪',
            'wood': '🪵',
            'laundry': '🧺',
            'playground': '🛝',
            
            // Access
            'pet': '🐕',
            'wheelchair': '♿',
            'big_rig': '🚛',
            'tent': '⛺',
            'generator': '⚙️'
        };
        
        // Try to match by icon field first, then fall back to name matching
        return iconMap[icon] || iconMap[amenityName?.toLowerCase()] || '🏕️';
    };

    const formatAmenities = (amenities) => {
        return amenities?.filter(amenity => amenity.available) || [];
    };

    const fetchReviews = async (forceRefresh = false) => {
        if (!forceRefresh && reviews.length > 0) return;

        try {
            setReviewsLoading(true);
            const response = await fetch(`/otgservices/getCampReviews.php?camp_location_id=${camp_location_id}&include_photos=1&_t=${Date.now()}`);
            const data = await response.json();

            if (data.success) {
                const reviewsData = Array.isArray(data.data) ? data.data : [];
                setReviews(reviewsData);
            } else {
                console.error('Failed to load reviews:', data.error?.message);
                setReviews([]);
            }
        } catch (err) {
            console.error('Network error loading reviews:', err);
            setReviews([]);
        } finally {
            setReviewsLoading(false);
        }
    };

    const fetchRecentCheckins = async () => {
        try {
            setCheckinsLoading(true);
            const response = await fetch(`/otgservices/getCampCheckins.php?camp_location_id=${camp_location_id}&status=all&limit=10&order_by=checkin_date&order_dir=DESC`);
            const data = await response.json();

            if (data.success) {
                setCheckins(data.data || []);
            } else {
                console.error('Failed to load check-ins:', data.error?.message);
                setCheckins([]);
            }
        } catch (err) {
            console.error('Network error loading check-ins:', err);
            setCheckins([]);
        } finally {
            setCheckinsLoading(false);
        }
    };

    const handleReviewsToggle = () => {
        setReviewsExpanded(!reviewsExpanded);
        if (!reviewsExpanded) {
            fetchReviews();
        }
    };

    const handleCheckIn = async () => {
        // Check if user is logged in
        if (!userId) {
            alert('You need to login to check in to this campsite.');
            return;
        }

        if (checkingIn) return; // Prevent double clicks

        try {
            setCheckingIn(true);

            const response = await fetch('/otgservices/addCampCheckin.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    camp_location_id: parseInt(camp_location_id),
                    user_id: parseInt(userId)
                })
            });

            const data = await response.json();

            if (data.success) {
                alert('Successfully checked in! Enjoy your stay!');
                // Refresh the check-ins list to show the new check-in
                fetchRecentCheckins();
            } else {
                alert('Check-in failed: ' + (data.error?.message || 'Unknown error'));
            }
        } catch (error) {
            console.error('Check-in error:', error);
            alert('Network error occurred during check-in. Please try again.');
        } finally {
            setCheckingIn(false);
        }
    };

    const handleReviewInputChange = (field, value) => {
        setNewReview(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleReviewPhotosChange = (photos) => {
        setReviewPhotos(photos);
        setNewReview(prev => ({
            ...prev,
            photos: photos
        }));
    };

    const uploadReviewPhotos = async (reviewId, photos) => {
        console.log('Uploading photos for review ID:', reviewId, 'Photos:', photos);
        try {
            for (let i = 0; i < photos.length; i++) {
                const photo = photos[i];
                console.log('Uploading photo:', i + 1, photo);
                
                const formData = new FormData();
                formData.append('review_id', reviewId);
                formData.append('photo', photo.file);
                formData.append('caption', photo.caption || '');
                formData.append('photo_type', photo.photo_type || 'general');
                formData.append('display_order', i + 1);

                const response = await fetch('/otgservices/uploadReviewPhoto.php', {
                    method: 'POST',
                    body: formData
                });

                const result = await response.json();
                console.log('Photo upload result:', result);

                if (!response.ok || !result.success) {
                    console.error('Failed to upload photo', i + 1, result);
                }
            }
        } catch (error) {
            console.error('Error uploading review photos:', error);
        }
    };

    const handleSubmitReview = async () => {
        if (!userId) {
            alert('Please log in to submit a review');
            return;
        }

        // Validate required fields
        if (!newReview.title.trim() || !newReview.review_text.trim()) {
            alert('Please fill in both title and review text');
            return;
        }

        setSubmittingReview(true);

        try {
            // First, submit the review
            const reviewPayload = {
                camp_location_id: camp_location_id,
                user_id: parseInt(userId),
                title: newReview.title.trim(),
                review_text: newReview.review_text.trim(),
                overall_rating: newReview.overall_rating,
                cleanliness_rating: newReview.cleanliness_rating,
                facilities_rating: newReview.facilities_rating,
                location_rating: newReview.location_rating,
                value_rating: newReview.value_rating,
                would_recommend: newReview.would_recommend,
                stay_duration_days: newReview.stay_duration_days,
                group_size: newReview.group_size,
                camping_type: newReview.camping_type
            };

            if (newReview.visit_date) {
                reviewPayload.visit_date = newReview.visit_date;
            }

            const response = await fetch('/otgservices/addCampReview.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(reviewPayload)
            });

            const reviewResult = await response.json();

            if (reviewResult.success && reviewResult.data?.review_id) {
                // Upload photos if any
                if (reviewPhotos && reviewPhotos.length > 0) {
                    await uploadReviewPhotos(reviewResult.data.review_id, reviewPhotos);
                }

                // Reset form
                setNewReview({
                    title: '',
                    review_text: '',
                    overall_rating: 5,
                    cleanliness_rating: 5,
                    facilities_rating: 5,
                    location_rating: 5,
                    value_rating: 5,
                    would_recommend: true,
                    visit_date: '',
                    stay_duration_days: 1,
                    group_size: 1,
                    camping_type: 'tent',
                    photos: []
                });
                setReviewPhotos([]);
                setShowAddReview(false);

                // Clear reviews to force reload
                setReviews([]);

                // First reload camp details (reviews summary)
                await fetchCampDetails();

                // Then wait a moment and reload individual reviews
                setTimeout(async () => {
                    await fetchReviews(true); // Force refresh
                }, 500);

                alert('Review submitted successfully!');
            } else {
                alert('Failed to submit review: ' + (reviewResult.error?.message || 'Unknown error'));
            }
        } catch (err) {
            console.error('Error submitting review:', err);
            alert('Network error occurred while submitting review');
        } finally {
            setSubmittingReview(false);
        }
    };

    const renderStarRating = (rating, onChange = null, size = 'h-4 w-4') => {
        return (
            <div className="flex items-center">
                {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                        key={star}
                        className={`${size} ${
                            star <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
                        } ${onChange ? 'cursor-pointer hover:text-yellow-300' : ''}`}
                        onClick={onChange ? () => onChange(star) : undefined}
                    />
                ))}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-red-600 text-xl">{error}</div>
            </div>
        );
    }

    const { basic_info, details, amenities, photos, reviews_summary, recent_reviews } = campData;
    const amenityList = formatAmenities(amenities);

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-orange-600 text-white px-6 py-4">
                <div className="max-w-7xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="bg-white p-2 rounded">
                            <Tent className="w-6 h-6 text-orange-600" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold">OTG Camp</h1>
                            <p className="text-sm opacity-90">OFF THE GRID</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        {/* Return to Map Button */}
                        {getReturnMapState() ? (
                            <button
                                onClick={goBackToMap}
                                className="bg-white text-orange-600 px-4 py-2 rounded-lg font-medium flex items-center gap-2 hover:bg-orange-50 transition-colors"
                                title={`Return to map at previous location (${getReturnMapState().lat.toFixed(4)}, ${getReturnMapState().lng.toFixed(4)})`}
                            >
                                <Map className="w-4 h-4" />
                                Return to Map
                            </button>
                        ) : (
                            <button
                                onClick={() => navigate('/events/camps')}
                                className="bg-white text-orange-600 px-4 py-2 rounded-lg font-medium flex items-center gap-2 hover:bg-orange-50 transition-colors"
                                title="Go to Camp Map"
                            >
                                <Map className="w-4 h-4" />
                                Camp Map
                            </button>
                        )}
                        
                        <button
                            onClick={() => navigate(`/events/camps/${camp_location_id}/edit/${userId}`)}
                            className="bg-white text-orange-600 px-4 py-2 rounded-lg font-medium flex items-center gap-2 hover:bg-orange-50 transition-colors"
                        >
                            <Edit className="w-4 h-4" />
                            Edit
                        </button>
                        <span className="text-sm bg-orange-700 px-2 py-1 rounded">Pro</span>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto p-6">
                {/* Camp Title and Rating */}
                <div className="mb-6">
                    <div className="flex items-center gap-2 text-blue-600 text-sm mb-2">
                        <span className="bg-blue-100 px-2 py-1 rounded text-xs">
                            {basic_info?.type === 'dispersed' ? 'Dispersed Camp' : 'Campground'}
                        </span>
                    </div>
                    <h1 className="text-3xl font-bold text-gray-900 mb-3">
                        {basic_info?.name || 'Camp Name'}
                    </h1>
                    {reviews_summary?.avg_overall_rating && (
                        <div className="mb-4">
                            {renderStars(
                                reviews_summary.avg_overall_rating,
                                reviews_summary.total_reviews
                            )}
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                    {/* Left Sidebar - Amenities and Camp Details */}
                    <div className="lg:col-span-1 space-y-4">
                        {/* Collapsible Amenities */}
                        <div className="bg-white rounded-lg shadow">
                            <div 
                                className="p-4 cursor-pointer flex items-center justify-between hover:bg-gray-50"
                                onClick={() => setAmenitiesExpanded(!amenitiesExpanded)}
                            >
                                <h2 className="text-lg font-semibold flex items-center gap-2">
                                    <Tent className="w-5 h-5 text-orange-600" />
                                    Amenities
                                </h2>
                                {amenitiesExpanded ? 
                                    <ChevronUp className="w-5 h-5 text-gray-400" /> : 
                                    <ChevronDown className="w-5 h-5 text-gray-400" />
                                }
                            </div>
                            {amenitiesExpanded && (
                                <div className="px-4 pb-4">
                                    {amenityList.length > 0 ? (
                                        <div className="grid grid-cols-4 gap-3">
                                            {amenityList.slice(0, 16).map((amenity, index) => (
                                                <div
                                                    key={amenity.id}
                                                    className="flex flex-col items-center p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                                    title={`${amenity.name}: ${amenity.description}`}
                                                >
                                                    <div className="text-lg mb-1">
                                                        {getAmenityIcon(amenity.name, amenity.icon)}
                                                    </div>
                                                    <span className="text-xs text-gray-700 text-center leading-tight font-medium">
                                                        {amenity.name}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-8 text-gray-500">
                                            <span className="text-2xl mb-2 block">🏕️</span>
                                            <p className="text-sm">No amenities available</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Camp Details */}
                        <div className="bg-white rounded-lg shadow p-4">
                            <div className="space-y-3 text-sm">
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-700">Site Size:</span>
                                    <span className="text-gray-900 font-medium">{details?.total_sites || '-'}</span>
                                </div>
                                
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-700">Is Level:</span>
                                    <span className="text-gray-900 font-medium">{details?.terrain || '-'}</span>
                                </div>
                                
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-700">Privacy:</span>
                                    <span className="text-gray-900 font-medium">{details?.privacy_level || '-'}</span>
                                </div>
                                
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-700">getCampSuitable for Tents:</span>
                                    <span className="text-gray-900 font-medium">{details?.tents}</span>
                                </div>
                                
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-700">Fire Ring:</span>
                                    <span className="text-gray-900 font-medium">{details?.fire_rings}</span>
                                </div>
                                
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-700">Potable Water:</span>
                                    <span className="text-gray-900 font-medium">{details?.water}</span>
                                </div>
                                
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-700">Picnic Table:</span>
                                    <span className="text-gray-900 font-medium">{details?.picnic_tables}</span>
                                </div>
                                
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-700">Toilets:</span>
                                    <span className="text-gray-900 font-medium">{details?.restrooms}</span>
                                </div>

                                
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-700">RV Friendly:</span>
                                    <span className="text-gray-900 font-medium">{details?.rv_friendly}</span>
                                </div>
                                
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-700">Pet Friendly:</span>
                                    <span className="text-gray-900 font-medium">{details?.pet_friendly}</span>
                                </div>
                                
                                <div className="flex justify-between items-start">
                                    <span className="text-gray-700">Land Management:</span>
                                    <span className="text-gray-900 font-medium text-right">{details?.managing_agency || '-'}</span>
                                </div>
                                
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-700">Stay Limit:</span>
                                    <span className="text-gray-900 font-medium">{details?.max_stay_days || '- '} days</span>
                                </div>
                                
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-700">Reservation Required:</span>
                                    <span className="text-gray-900 font-medium">{details?.reservation_required}</span>
                                </div>

                                
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-700">Cell Service:</span>
                                    <span className="text-gray-900 font-medium">{details?.cellservice}</span>
                                </div>

                                <div className="flex justify-between items-center">
                                    <span className="text-gray-700">Noise Level:</span>
                                    <span className="text-gray-900 font-medium">{details?.noise_level || 'Unknown'}</span>
                                </div>

                                <div className="flex justify-between items-center">
                                    <span className="text-gray-700">Wildlife Activity:</span>
                                    <span className="text-gray-900 font-medium">Unknown</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Main Content - Description and Photos */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Description */}
                        <div className="bg-white rounded-lg shadow p-6">
                            <h2 className="text-xl font-semibold mb-4">Description</h2>
                            <p className="text-gray-700 leading-relaxed">
                                {basic_info?.description || details?.description || 
                                 'No description available for this camping location.'}
                            </p>
                        </div>

                        {/* User Submitted Photos */}
                        <div className="bg-white rounded-lg shadow p-6">
                            <h2 className="text-xl font-semibold mb-4">User Submitted Photos</h2>
                            {photos && photos.length > 0 ? (
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                    {photos.slice(0, 7).map((photo, index) => (
                                        <div 
                                            key={photo.id} 
                                            className="aspect-square rounded-lg overflow-hidden cursor-pointer"
                                            onClick={() => setSelectedPhoto(photo)}
                                        >
                                            <img
                                                src={photo.photo_url}
                                                alt={photo.caption || 'Camp photo'}
                                                className="w-full h-full object-cover hover:scale-105 transition-transform duration-200"
                                            />
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-12 text-gray-500">
                                    <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-lg flex items-center justify-center">
                                        <Tent className="w-8 h-8 text-gray-400" />
                                    </div>
                                    <p>No photos available yet</p>
                                </div>
                            )}
                        </div>

                        {/* Reviews & Feedback Section */}
                        <div className="bg-gray-50 rounded-lg overflow-hidden">
                            <button
                                onClick={handleReviewsToggle}
                                className="w-full px-4 py-4 flex items-center justify-between hover:bg-blue-100 transition-colors bg-blue-50"
                            >
                                <div className="flex items-center">
                                    <MessageSquare className="h-5 w-5 mr-2 text-blue-600" />
                                    <h3 className="text-lg font-semibold text-blue-800">Reviews & Feedback</h3>
                                    <span className="ml-2 text-sm text-blue-600">
                                        ({reviews_summary?.total_reviews || 0} reviews)
                                    </span>
                                </div>
                                {reviewsExpanded ? (
                                    <ChevronUp className="h-5 w-5 text-blue-500" />
                                ) : (
                                    <ChevronDown className="h-5 w-5 text-blue-500" />
                                )}
                            </button>

                            {reviewsExpanded && (
                                <div className="px-4 pb-4 space-y-4">
                                    {/* Reviews Summary */}
                                    {reviews_summary && (
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-white p-4 rounded-lg">
                                            <div className="text-center">
                                                <div className="text-2xl font-bold text-blue-600">
                                                    {reviews_summary.avg_overall_rating || 'N/A'}
                                                </div>
                                                <div className="text-sm text-gray-600">Average Rating</div>
                                                {reviews_summary.avg_overall_rating && (
                                                    <div className="flex justify-center mt-1">
                                                        {renderStars(Math.round(reviews_summary.avg_overall_rating), 0)}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="text-center">
                                                <div className="text-2xl font-bold text-green-600">
                                                    {reviews_summary.total_reviews || 0}
                                                </div>
                                                <div className="text-sm text-gray-600">Total Reviews</div>
                                            </div>
                                            <div className="text-center">
                                                <div className="text-2xl font-bold text-purple-600">
                                                    {reviews_summary.recommendation_percentage || 0}%
                                                </div>
                                                <div className="text-sm text-gray-600">Would Recommend</div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Add Review Button */}
                                    <div className="flex justify-between items-center">
                                        <h4 className="font-medium text-gray-900">Recent Reviews</h4>
                                        {userId ? (
                                            <button
                                                onClick={() => setShowAddReview(!showAddReview)}
                                                className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                                            >
                                                <Plus className="h-4 w-4 mr-1" />
                                                Add Review
                                            </button>
                                        ) : (
                                            <div className="flex items-center space-x-2">
                                                <span className="text-sm text-gray-500">Please log in to add a review</span>
                                                <button className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50">
                                                    Log In
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    {/* Add Review Form */}
                                    {(showAddReview && userId) && (
                                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                                            <h5 className="font-medium mb-3">Write a Review</h5>
                                            
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                                <div>
                                                    <label className="block text-sm font-medium mb-1">Review Title</label>
                                                    <input
                                                        type="text"
                                                        value={newReview.title}
                                                        onChange={(e) => handleReviewInputChange('title', e.target.value)}
                                                        className="w-full p-2 border border-gray-300 rounded-md text-sm"
                                                        placeholder="Brief title for your review"
                                                    />
                                                </div>
                                                
                                                <div>
                                                    <label className="block text-sm font-medium mb-1">Visit Date</label>
                                                    <input
                                                        type="date"
                                                        value={newReview.visit_date}
                                                        onChange={(e) => handleReviewInputChange('visit_date', e.target.value)}
                                                        className="w-full p-2 border border-gray-300 rounded-md text-sm"
                                                    />
                                                </div>
                                            </div>

                                            <div className="mb-4">
                                                <label className="block text-sm font-medium mb-1">Review</label>
                                                <textarea
                                                    value={newReview.review_text}
                                                    onChange={(e) => handleReviewInputChange('review_text', e.target.value)}
                                                    className="w-full p-2 border border-gray-300 rounded-md text-sm"
                                                    rows="3"
                                                    placeholder="Share your experience at this campground..."
                                                />
                                            </div>

                                            {/* Photo Upload Component */}
                                            <div className="mb-4">
                                                <ReviewPhotoUpload
                                                    onPhotosChange={handleReviewPhotosChange}
                                                    maxPhotos={5}
                                                    existingPhotos={reviewPhotos}
                                                />
                                            </div>

                                            {/* Ratings */}
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                                <div>
                                                    <label className="block text-sm font-medium mb-2">Overall Rating</label>
                                                    {renderStarRating(newReview.overall_rating, (rating) => handleReviewInputChange('overall_rating', rating), 'h-5 w-5')}
                                                </div>
                                                
                                                <div>
                                                    <label className="block text-sm font-medium mb-2">Cleanliness</label>
                                                    {renderStarRating(newReview.cleanliness_rating, (rating) => handleReviewInputChange('cleanliness_rating', rating), 'h-5 w-5')}
                                                </div>
                                                
                                                <div>
                                                    <label className="block text-sm font-medium mb-2">Facilities</label>
                                                    {renderStarRating(newReview.facilities_rating, (rating) => handleReviewInputChange('facilities_rating', rating), 'h-5 w-5')}
                                                </div>
                                                
                                                <div>
                                                    <label className="block text-sm font-medium mb-2">Location</label>
                                                    {renderStarRating(newReview.location_rating, (rating) => handleReviewInputChange('location_rating', rating), 'h-5 w-5')}
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                                <div>
                                                    <label className="block text-sm font-medium mb-1">Stay Duration (nights)</label>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        value={newReview.stay_duration_days}
                                                        onChange={(e) => handleReviewInputChange('stay_duration_days', parseInt(e.target.value))}
                                                        className="w-full p-2 border border-gray-300 rounded-md text-sm"
                                                    />
                                                </div>
                                                
                                                <div>
                                                    <label className="block text-sm font-medium mb-1">Group Size</label>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        value={newReview.group_size}
                                                        onChange={(e) => handleReviewInputChange('group_size', parseInt(e.target.value))}
                                                        className="w-full p-2 border border-gray-300 rounded-md text-sm"
                                                    />
                                                </div>
                                                
                                                <div>
                                                    <label className="block text-sm font-medium mb-1">Camping Type</label>
                                                    <select
                                                        value={newReview.camping_type}
                                                        onChange={(e) => handleReviewInputChange('camping_type', e.target.value)}
                                                        className="w-full p-2 border border-gray-300 rounded-md text-sm"
                                                    >
                                                        <option value="tent">Tent</option>
                                                        <option value="rv">RV</option>
                                                        <option value="car_camping">Car Camping</option>
                                                        <option value="backpacking">Backpacking</option>
                                                        <option value="other">Other</option>
                                                    </select>
                                                </div>
                                            </div>

                                            <div className="mb-4">
                                                <label className="flex items-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={newReview.would_recommend}
                                                        onChange={(e) => handleReviewInputChange('would_recommend', e.target.checked)}
                                                        className="h-4 w-4 text-blue-600 mr-2"
                                                    />
                                                    <span className="text-sm font-medium">I would recommend this campground</span>
                                                </label>
                                            </div>

                                            <div className="flex justify-end space-x-2">
                                                <button
                                                    onClick={() => {
                                                        setShowAddReview(false);
                                                        setReviewPhotos([]);
                                                    }}
                                                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    onClick={handleSubmitReview}
                                                    disabled={submittingReview || !newReview.title || !newReview.review_text}
                                                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm"
                                                >
                                                    {submittingReview ? (
                                                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                                    ) : (
                                                        <Send className="h-4 w-4 mr-1" />
                                                    )}
                                                    {submittingReview ? 'Submitting...' : 'Submit Review'}
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Reviews List */}
                                    <div className="space-y-3">
                                        {reviewsLoading ? (
                                            <div className="text-center py-4">
                                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto"></div>
                                                <span className="text-sm text-gray-600 mt-2">Loading reviews...</span>
                                            </div>
                                        ) : reviews && reviews.length > 0 ? (
                                            <>
                                                {reviews.map((review) => (
                                                    <div key={review.id} className="bg-white p-4 rounded-lg border border-gray-200">
                                                        <div className="flex justify-between items-start mb-2">
                                                            <div>
                                                                <h5 className="font-medium">{review.title || 'Untitled Review'}</h5>
                                                                <p className="text-sm text-gray-600">
                                                                    By {review.username || review.display_name || 'Anonymous'} • {review.visit_date || 'No date'}
                                                                </p>
                                                            </div>
                                                            <div className="flex items-center">
                                                                <div className="flex">
                                                                    {[...Array(5)].map((_, i) => (
                                                                        <Star
                                                                            key={i}
                                                                            className={`w-4 h-4 ${
                                                                                i < (review.overall_rating || 0)
                                                                                    ? 'fill-yellow-400 text-yellow-400'
                                                                                    : 'text-gray-300'
                                                                            }`}
                                                                        />
                                                                    ))}
                                                                </div>
                                                                <span className="ml-1 text-sm text-gray-600">({review.overall_rating || 0})</span>
                                                            </div>
                                                        </div>
                                                        <p className="text-gray-700 text-sm mb-2">{review.review_text || 'No review text'}</p>
                                                        {/* Review Photos Display */}
                                                        <ReviewPhotosDisplay photos={review.photos || []} />
                                                        <div className="flex justify-between items-center text-xs text-gray-500 mt-3">
                                                            <span>
                                                                {review.stay_duration_days || 0} nights • {review.group_size || 0} guests • {review.camping_type || 'Unknown'}
                                                            </span>
                                                            <div className="flex items-center space-x-2">
                                                                {review.photos && review.photos.length > 0 && (
                                                                    <span className="text-blue-600">📷 {review.photos.length} photo{review.photos.length !== 1 ? 's' : ''}</span>
                                                                )}
                                                                {review.would_recommend && (
                                                                    <span className="text-green-600">Recommends</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </>
                                        ) : (
                                            <div className="text-center py-8 text-gray-500">
                                                <MessageSquare className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                                                <p>No reviews yet. Be the first to review this campground!</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right Sidebar */}
                    <div className="lg:col-span-1 space-y-6">
                        {/* Submission Info */}
                        <div className="bg-white rounded-lg shadow p-4">
                            <div className="text-right text-sm text-gray-600">
                                <div className="font-medium">Submitted by: {createdBy || 'Unknown'}</div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="space-y-3">
                            <button 
                                onClick={handleCheckIn}
                                disabled={checkingIn}
                                className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                                    checkingIn 
                                        ? 'bg-gray-400 cursor-not-allowed text-white' 
                                        : 'bg-blue-500 hover:bg-blue-600 text-white'
                                }`}
                            >
                                {checkingIn ? 'Checking In...' : 'Check-In'}
                            </button>

                            <button
                                onClick={() => navigate(`/events/site_report/${camp_location_id}?site_name=${encodeURIComponent(basic_info?.name || 'Unknown Site')}`)}
                                className="w-full bg-red-500 hover:bg-red-600 text-white py-3 px-4 rounded-lg font-medium flex items-center justify-center gap-2"
                                title="Report an issue with this site"
                            >
                                <AlertTriangle className="w-4 h-4" />
                                Report this Site
                            </button>
                        </div>

                        {/* Recent Check-ins */}
                        <div className="bg-white rounded-lg shadow p-4">
                            <h3 className="font-semibold mb-4">Recent Check-ins</h3>
                            {checkinsLoading ? (
                                <div className="text-center py-4">
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500 mx-auto"></div>
                                    <span className="text-xs text-gray-600 mt-2">Loading check-ins...</span>
                                </div>
                            ) : checkins && checkins.length > 0 ? (
                                <div className="space-y-4">
                                    {checkins.slice(0, 5).map((checkin) => (
                                        <div key={checkin.id} className="border-b border-gray-100 pb-3 last:border-b-0">
                                            <div className="flex justify-between items-start mb-2">
                                                <div className="font-medium text-sm">{checkin.username || 'Anonymous'}</div>
                                                <div className="text-xs text-gray-500">
                                                    {checkin.checkin_date ? new Date(checkin.checkin_date).toLocaleDateString() : 'No date'}
                                                </div>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <div className="text-xs text-gray-600">
                                                    <span className="capitalize">{checkin.camping_type || 'tent'}</span>
                                                    {checkin.group_size > 1 && (
                                                        <span> • {checkin.group_size} guests</span>
                                                    )}
                                                </div>
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                    checkin.status === 'active' 
                                                        ? 'bg-green-100 text-green-800'
                                                        : checkin.status === 'completed'
                                                        ? 'bg-blue-100 text-blue-800'
                                                        : 'bg-gray-100 text-gray-800'
                                                }`}>
                                                    {checkin.status}
                                                </span>
                                            </div>
                                            {checkin.notes && (
                                                <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                                                    {checkin.notes}
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-gray-500 text-sm">No recent check-ins</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Photo Modal */}
            {selectedPhoto && (
                <div 
                    className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
                    onClick={() => setSelectedPhoto(null)}
                >
                    <div className="max-w-4xl max-h-full relative">
                        <button
                            onClick={() => setSelectedPhoto(null)}
                            className="absolute top-4 right-4 text-white hover:text-gray-300 z-10 bg-black bg-opacity-50 rounded-full p-2"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                        <img
                            src={selectedPhoto.photo_url}
                            alt={selectedPhoto.caption || 'Camp photo'}
                            className="max-w-full max-h-full object-contain rounded-lg"
                            onClick={(e) => e.stopPropagation()}
                        />
                        {selectedPhoto.caption && (
                            <div className="absolute bottom-4 left-4 right-4 bg-black bg-opacity-50 text-white p-2 rounded">
                                <p className="text-sm">{selectedPhoto.caption}</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CampDetailsView;